import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  startCall,
  getPrimaryAgent,
  setPrimaryAgent,
  getAgentHistory,
  callTopics,
  getPhonelyProviders,
} from "service/axiosConfig";
import PropTypes from "prop-types";
import { Box, Typography, Card, CardContent, Button, Chip } from "@mui/material";

function DemoBeta() {
  const VALID_CODE = "heartline2025!!";
  const [authCode, setAuthCode] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [topics, setTopics] = useState([]);
  const [formData, setFormData] = useState({
    topics: [],
    followUp: false,
    agentType: "", // "ElevenLabs" or "Phonely"
    provider: "", // e.g. "Deepgram" if Phonely selected
    voiceId: "", // backend-mapped voiceId
  });

  const [availableVoices, setAvailableVoices] = useState([]); // backend voice list [{provider, voiceId}]

  // ─── LOAD PROVIDER & VOICE IDS FROM BACKEND ─────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // your backend endpoint now returns [{ provider, voiceId }]
        const res = await getPhonelyProviders();
        setAvailableVoices(res || []);
      } catch (err) {
        console.error("❌ Voice list fetch failed:", err);
        toast.error("Failed to load voice list");
      }
    })();
  }, []);

  // ─── HANDLER FOR PROVIDER CHANGE ─────────────────────────────
  const handleProviderChange = (provider) => {
    setSelectedProvider(provider);
    const match = availableVoices.find((v) => v.provider === provider);
    if (match) {
      setFormData((prev) => ({ ...prev, primaryAgent: match.voiceId }));
    }
  };

  const [agentHistory, setAgentHistory] = useState([]);

  //
  // ─── LOAD TOPICS ───────────────────────────────────────────────
  //
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await callTopics();
        if (res) setTopics(res);
      } catch (err) {
        console.error("Failed to load topics", err);
        toast.error("Failed to load topics");
      }
    };
    fetchTopics();
  }, []);

  //
  // ─── LOAD DEFAULT PRIMARY AGENT ────────────────────────────────
  //
  useEffect(() => {
    const fetchPrimaryAgent = async () => {
      try {
        const res = await getPrimaryAgent();
        if (!res) return;

        // res now includes { agentType, provider, voiceId }
        setFormData((prev) => ({
          ...prev,
          agentType: res.agentType || "ElevenLabs",
          provider: res.provider || "",
          voiceId: res.voiceId || "",
        }));
      } catch (err) {
        console.error("Failed to load primary agent", err);
        toast.error("Failed to load current agent");
      }
    };
    fetchPrimaryAgent();
  }, []);

  //
  // ─── LOAD AGENT HISTORY ────────────────────────────────────────
  //
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getAgentHistory();
        setAgentHistory(res || []);
      } catch (err) {
        console.error("Failed to load agent history", err);
      }
    };
    fetchHistory();
  }, []);

  //
  // ─── HANDLE SUBMIT (START CALL) ────────────────────────────────
  //
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!name.trim()) newErrors.name = "Please enter your name";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) newErrors.email = "Please enter your email";
    else if (!emailRegex.test(email)) newErrors.email = "Enter a valid email address";
    if (!phone || !isValidPhoneNumber(phone)) newErrors.phone = "Please enter a valid phone number";
    if (formData.topics.length === 0) newErrors.topics = "Please select at least one topic";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const payload = {
        phoneNumber: phone,
        name: name.trim(),
        email: email.trim(),
        followUp: formData.followUp,
        topics: formData.topics,
        // ❌ no primaryAgent here anymore — backend uses stored value
      };
      await startCall(payload);
      toast.success("Demo call started!");
    } catch (error) {
      console.error("❌ Error starting call:", error);
      toast.error("Failed to start call");
    } finally {
      setIsSubmitting(false);
    }
  };

  //
  // ─── HANDLE AGENT SAVE ─────────────────────────────────────────
  //
  const handleSaveAgent = async () => {
    try {
      const { agentType, provider, voiceId } = formData;

      if (!agentType) {
        toast.warn("Please select an agent type.");
        return;
      }

      // For Phonely, provider and voiceId are required
      if (agentType === "Phonely" && (!provider || !voiceId)) {
        toast.warn("Please select a provider for Phonely.");
        return;
      }

      await setPrimaryAgent({ agentType, provider, voiceId });
      toast.success("Primary agent updated successfully");

      const history = await getAgentHistory();
      setAgentHistory(history);
    } catch (err) {
      console.error("Failed to update agent", err);
      toast.error("Failed to update agent");
    }
  };

  //
  // ─── RENDER ───────────────────────────────────────────────────
  //
  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      {/* Background */}
      <Box
        sx={{
          filter: isAuthorized ? "none" : "blur(8px)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(to bottom right, #fff7f0, #ffffff, #f0fff7)",
          fontFamily:
            'ui-sans-serif, system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            borderBottom: "1px solid #e2e8f0",
            bgcolor: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(4px)",
            py: 1.5,
            textAlign: "center",
          }}
        >
          <Typography
            fontWeight="bold"
            sx={{ fontSize: 28, color: "#0f172a", fontFamily: "inherit" }}
          >
            Heartline Control Panel
          </Typography>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            gap: 3,
            px: 2,
            py: 6,
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          {/* ─── DEMO CALL CARD ─────────────────────────────── */}
          <Card
            variant="outlined"
            sx={{
              flex: "1 1 55%",
              borderColor: "#fbbf24 !important",
              borderWidth: "2px !important",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography fontWeight="600" fontSize="22px" sx={{ mb: 2, color: "#7c2d12" }}>
                Interactive Demo Call
              </Typography>
              <Typography sx={{ mb: 3, color: "#475569", fontSize: 15 }}>
                Try a sample Heartline call and see how the AI interacts.
              </Typography>

              <form onSubmit={handleSubmit}>
                {/* Name */}
                <InputField
                  label="Name"
                  value={name}
                  onChange={setName}
                  id="name"
                  placeholder="Enter your name"
                  error={errors.name}
                />
                {/* Email */}
                <InputField
                  label="Email Address"
                  value={email}
                  onChange={setEmail}
                  id="email"
                  placeholder="Enter your email"
                  error={errors.email}
                />
                {/* Phone */}
                <Box mb={2}>
                  <Typography
                    component="label"
                    htmlFor="phone"
                    sx={{ fontWeight: 500, fontSize: 14, color: "#0f172a" }}
                  >
                    Mobile Phone #
                  </Typography>
                  <PhoneInput
                    id="phone"
                    value={phone}
                    onChange={setPhone}
                    defaultCountry="US"
                    countries={["US"]}
                    disableCountryPicker
                    placeholder="Enter your mobile phone number"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#f8fafc",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: "0.9rem",
                    }}
                  />
                  {errors.phone && <Typography sx={errorStyle}>{errors.phone}</Typography>}
                </Box>

                {/* Follow-up */}
                <Box mb={2} display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <input
                    type="checkbox"
                    id="followUp"
                    checked={formData.followUp}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        followUp: e.target.checked,
                      }))
                    }
                    style={{ transform: "scale(1.1)", cursor: "pointer" }}
                  />
                  <Typography sx={{ fontSize: 14, color: "#0f172a" }}>
                    Follow-up required?
                  </Typography>
                </Box>

                {/* Topics */}
                <Box mb={2}>
                  <Typography fontWeight={500} fontSize={14} mb={1}>
                    Select Topics (max 3)
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2,1fr)",
                        sm: "repeat(3,1fr)",
                      },
                      gap: 1.2,
                    }}
                  >
                    {topics.map((item) => {
                      const selected = formData.topics.includes(item.id);
                      return (
                        <Chip
                          key={item.id}
                          label={item.name}
                          clickable
                          onClick={() =>
                            setFormData((prev) => {
                              if (selected)
                                return {
                                  ...prev,
                                  topics: prev.topics.filter((id) => id !== item.id),
                                };
                              if (prev.topics.length >= 3) {
                                toast.info("You can select max 3 topics");
                                return prev;
                              }
                              return {
                                ...prev,
                                topics: [...prev.topics, item.id],
                              };
                            })
                          }
                          sx={{
                            backgroundColor: selected ? "rgb(86,25,25)" : "#f0f0f0",
                            color: selected ? "#fff" : "#000",
                            fontSize: 12,
                            borderRadius: "6px",
                            height: 28,
                          }}
                        />
                      );
                    })}
                  </Box>
                  {errors.topics && <Typography color="error">{errors.topics}</Typography>}
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    mt: 2,
                    bgcolor: "#7c2d12",
                    "&:hover": { bgcolor: "#65200d" },
                    py: 0.8,
                    fontWeight: 600,
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Start Demo Experience
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* ─── AGENT CONTROL PANEL ─────────────────────────── */}
          <Card
            variant="outlined"
            sx={{
              flex: "1 1 40%",
              borderColor: "#86efac !important",
              borderWidth: "2px !important",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: "#166534" }}>
                Manage Primary Agent
              </Typography>

              {/* ─── Agent Type Dropdown ─────────────────────────── */}
              <Box display="flex" gap={1} alignItems="center" mb={2}>
                <Box
                  component="select"
                  value={formData.agentType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      agentType: e.target.value,
                      provider: e.target.value === "Phonely" ? prev.provider : "",
                    }))
                  }
                  sx={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontFamily: "inherit",
                  }}
                >
                  <option value="">Select Agent Type</option>
                  <option value="ElevenLabs">ElevenLabs</option>
                  <option value="Phonely">Phonely</option>
                </Box>
              </Box>

              {/* ─── Provider Dropdown (only when Phonely) ─────────────────────────── */}
              {formData.agentType === "Phonely" && (
                <Box display="flex" gap={1} alignItems="center" mb={2}>
                  <Box
                    component="select"
                    value={formData.provider}
                    onChange={(e) => {
                      const provider = e.target.value;
                      const match = availableVoices.find((v) => v.provider === provider);
                      setFormData((prev) => ({
                        ...prev,
                        provider,
                        voiceId: match ? match.voiceId : "",
                      }));
                    }}
                    sx={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      fontFamily: "inherit",
                    }}
                  >
                    <option value="">Select Provider</option>
                    {availableVoices.map((v) => (
                      <option key={v.voiceId} value={v.provider}>
                        {v.provider}
                      </option>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ─── Save Button ─────────────────────────── */}
              <Button
                variant="contained"
                onClick={handleSaveAgent}
                sx={{
                  bgcolor: "#166534",
                  "&:hover": { bgcolor: "#14532d" },
                  color: "#ffffff",
                  textTransform: "none",
                  fontFamily: "inherit",
                }}
              >
                Save
              </Button>

              <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
                Recent Changes
              </Typography>
              <Box
                sx={{
                  maxHeight: 250,
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 1.5,
                  background: "#f8fafc",
                }}
              >
                {agentHistory?.length === 0 ? (
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>No history yet</Typography>
                ) : (
                  agentHistory.map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        borderBottom: "1px solid #e2e8f0",
                        py: 0.6,
                        fontSize: "0.85rem",
                      }}
                    >
                      <strong>{h.agentType}</strong> – <strong>{h.switch}</strong> –{" "}
                      <span style={{ color: "#475569" }}>
                        {new Date(h.changedAt).toLocaleString()}
                      </span>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Auth Overlay */}
      {!isAuthorized && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            backdropFilter: "blur(4px)",
            marginTop: "100px",
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Enter Access Code</Typography>
          <Box
            component="input"
            type="password"
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value)}
            placeholder="Enter authentication code"
            sx={{
              width: 260,
              textAlign: "center",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              fontFamily: "inherit",
              fontSize: "14px",
              outline: "none",
              mb: 2,
              "&:focus": {
                borderColor: "#7c2d12",
                boxShadow: "0 0 0 1px #7c2d12",
              },
            }}
          />
          <Button
            variant="contained"
            sx={{
              bgcolor: "#7c2d12",
              "&:hover": { bgcolor: "#65200d" },
              color: "#ffffff",
              textTransform: "none",
              fontFamily: "inherit",
              px: 3,
            }}
            onClick={() => {
              if (authCode.trim() === VALID_CODE) {
                setIsAuthorized(true);
                toast.success("✅ Access granted");
              } else toast.error("❌ Invalid authentication code");
            }}
          >
            Unlock Demo
          </Button>
        </Box>
      )}
    </Box>
  );
}

/* ─── SMALL REUSABLE INPUT COMPONENT ───────────────────────── */
const InputField = ({ id, label, value, onChange, placeholder, error }) => (
  <Box textAlign="left" mb={2}>
    <Typography
      component="label"
      htmlFor={id}
      sx={{ fontWeight: 500, fontSize: 14, color: "#0f172a" }}
    >
      {label}
    </Typography>
    <Box
      component="input"
      id={id}
      type={id === "email" ? "email" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required
      sx={inputStyle}
    />
    {error && <Typography sx={errorStyle}>{error}</Typography>}
  </Box>
);

/* Shared Styles */
const inputStyle = {
  width: "100%",
  fontSize: "0.9rem",
  fontFamily: "inherit",
  padding: "10px 12px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  outline: "none",
  boxSizing: "border-box",
  "&:focus": { borderColor: "#7c2d12", boxShadow: "0 0 0 1px #7c2d12" },
};
const errorStyle = {
  color: "red",
  fontSize: "0.75rem",
  marginTop: "0.25rem",
};

InputField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
};

export default DemoBeta;
